import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setTempAuthToken, setToken } from '@/store/slices/authSlice';
import toast from 'react-hot-toast';
import { LoginFormData, loginSchema } from '@/utils/validations';
import CustomInput from '@/components/common/CustomInput';
import CaptchaBox from '@/components/common/CaptchaBox';
// import CaptchaBox from '@/components/common/CaptchaBox';

function LoginPage() {
  const [captchaId, setCaptchaId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const payload = {
        username: data.username,
        password: data.password,
        captchaId: captchaId,
        captchaText: data.captcha,
      };
      const res = await login(payload).unwrap();
      if (res?.accessToken) {
        dispatch(setToken(res.accessToken));
        reset();
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setRefreshKey((prev) => prev + 1);
      console.error('Login failed:', err);
      toast.error(err?.data?.message || 'Login failed!');
    }
  };

  return (
    <div className="mx-auto w-full">
      <div className="flex flex-col gap-y-4">
          <div className="text-center">
            <div className="mb-4 inline-flex rounded-2xl border border-primary/30 bg-primary/10 p-3 shadow-[0_0_40px_-18px_rgba(255,184,12,0.8)]">
              <img src="/images/logo.webp" alt="Brand logo" className="max-w-[200px]" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.38em] text-primary/90">
              Secure access
            </p>
            <h1 className="mt-3 text-2xl font-semibold text-white sm:text-3xl">Welcome back</h1>
            <p className="mt-2 text-sm text-white/65">
              Sign in to continue to your dashboard with a faster, cleaner experience.
            </p>
          </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <CustomInput
            name="username"
            type="text"
            control={control}
            label="Username*"
            placeholder="Enter your username"
            autoComplete="username"
            labelClassName="text-white/90 text-[13px] font-medium"
            inputClassName="rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-primary/30"
          />

          <CustomInput
            name="password"
            control={control}
            label="Password*"
            type="password"
            placeholder="Enter your password"
            autoComplete="current-password"
            labelClassName="text-white/90 text-[13px] font-medium"
            inputClassName="rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-primary/30"
            inputRules={{
              noSpace: true,
              maxLength: 20,
            }}
          />

          <CustomInput
            name="captcha"
            control={control}
            label="Captcha*"
            placeholder="Enter the characters shown"
            labelClassName="text-white/90 text-[13px] font-medium"
            inputClassName="rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-primary/30"
            inputRules={{
              noSpace: true,
              onlyNumber: true,
              maxLength: 5,
            }}
          />

          <CaptchaBox onCaptchaChange={setCaptchaId} refreshTrigger={refreshKey} />

          <div className="pt-2">
            <button
              type="submit"
              className="flex w-full items-center justify-center rounded-xl bg-primary px-3 py-3 text-base font-semibold text-primary-foreground shadow-[0_12px_35px_-12px_rgba(255,184,12,0.9)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-primary/90"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'LOGIN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
export default LoginPage;
